import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useActiveDatabaseRevision } from '../database/ActiveDatabaseContext';
import { databaseManager } from '../database/databaseManager';
import { syncBuildEnabled } from '../config/sync';
import { SyncOutboxWakeCoordinator } from './syncOutboxWake';
import { syncService, type SyncService } from './syncService';
import type { SyncRuntimeStatus } from './types';

const disabledStatus: SyncRuntimeStatus = {
  activity: 'disabled', effectiveEnabled: false, remoteRolloutStatus: 'not_checked',
  bootstrapState: 'not_started', openConflictCount: 0, canResetTechnicalState: false
};

interface SyncContextValue {
  status: SyncRuntimeStatus;
  runNow(): Promise<void>;
  resetTechnicalState(): Promise<void>;
}

const SyncContext = createContext<SyncContextValue>({
  status: disabledStatus,
  runNow: async () => undefined,
  resetTechnicalState: async () => undefined
});

export const SyncProvider = ({
  children,
  service = syncService
}: PropsWithChildren<{ service?: SyncService }>) => {
  const auth = useAuth();
  const databaseRevision = useActiveDatabaseRevision();
  const [status, setStatus] = useState<SyncRuntimeStatus>(disabledStatus);
  const wakeCoordinator = useRef<SyncOutboxWakeCoordinator | undefined>(undefined);
  const userId = auth.user?.id;

  const refresh = useCallback(async () => {
    if (!userId || !syncBuildEnabled) {
      setStatus(disabledStatus);
      return;
    }
    setStatus((current) => ({ ...current, activity: current.bootstrapState === 'complete' ? 'syncing' : 'bootstrapping' }));
    try {
      await service.synchronize(userId);
    } catch {
      // The service stores only its technical error code; user data never enters logs.
    } finally {
      setStatus(await service.getStatus());
    }
  }, [service, userId]);

  useEffect(() => {
    let active = true;
    const update = () => { if (active) void service.getStatus().then(setStatus); };
    const unsubscribe = service.subscribe(update);
    update();
    const coordinator = userId && syncBuildEnabled
      ? new SyncOutboxWakeCoordinator({
          db: databaseManager.getActiveDatabase(),
          synchronize: refresh
        })
      : undefined;
    wakeCoordinator.current = coordinator;
    coordinator?.start();
    if (coordinator) queueMicrotask(() => void coordinator.runNow());
    const online = () => void (coordinator?.runNow() ?? refresh());
    const visible = () => {
      if (document.visibilityState === 'visible') void (coordinator?.runNow() ?? refresh());
    };
    window.addEventListener('online', online);
    document.addEventListener('visibilitychange', visible);
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void (coordinator?.runNow() ?? refresh());
    }, 60_000);
    const channel = typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(`leitour-sync:${userId ?? 'anonymous'}`);
    if (channel) channel.onmessage = update;
    return () => {
      active = false;
      coordinator?.stop();
      if (wakeCoordinator.current === coordinator) wakeCoordinator.current = undefined;
      unsubscribe();
      window.removeEventListener('online', online);
      document.removeEventListener('visibilitychange', visible);
      window.clearInterval(interval);
      channel?.close();
    };
  }, [databaseRevision, refresh, service, userId]);

  const runNow = useCallback(async () => {
    await (wakeCoordinator.current?.runNow() ?? refresh());
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(`leitour-sync:${userId ?? 'anonymous'}`);
      channel.postMessage({ type: 'technical-state-changed' });
      channel.close();
    }
  }, [refresh, userId]);

  const resetTechnicalState = useCallback(async () => {
    if (!userId) throw new Error('Entre na conta correta antes de reiniciar a sincronização.');
    await service.resetTechnicalState(userId);
    setStatus(await service.getStatus());
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(`leitour-sync:${userId}`);
      channel.postMessage({ type: 'technical-state-changed' });
      channel.close();
    }
  }, [service, userId]);

  const value = useMemo(() => ({ status, runNow, resetTechnicalState }), [resetTechnicalState, runNow, status]);
  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSyncRuntime = (): SyncContextValue => useContext(SyncContext);
